import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Camera, PhoneCall, Building2, Siren } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Customer SR status palette
const statusStyles = {
  pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
  scheduled: 'text-blue-400 bg-blue-500/10',
  completed: 'text-green-400 bg-green-500/10',
  cancelled: 'text-red-400 bg-red-500/10',
};

// PM SR status palette — maps the rich PM status flow to a compact UI
const pmOpenStatuses = new Set(['Submitted', 'Scheduled', 'Technician Assigned', 'En Route', 'On Site', 'Waiting Approval']);

const AdminServiceRequests = () => {
  const [customerItems, setCustomerItems] = useState([]);
  const [pmItems, setPmItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [source, setSource] = useState('all'); // 'all' | 'customer' | 'pm'
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      // Parallel — customer + PM service requests merged into one tab so dispatch sees both.
      const [custRes, pmRes] = await Promise.allSettled([
        axios.get(`${API}/admin/service-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/pm/service-requests`, { withCredentials: true }),
      ]);
      if (custRes.status === 'fulfilled') setCustomerItems(custRes.value.data || []);
      else toast.error(custRes.reason?.response?.data?.detail || 'Failed to load customer requests');
      if (pmRes.status === 'fulfilled') setPmItems(pmRes.value.data || []);
      // PM endpoint may 403 for non-admin tiers — silently ignore
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load service requests');
    }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/service-requests/${id}?status=${status}`, {}, { withCredentials: true });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  // Normalize both sources into one display shape
  const unified = useMemo(() => {
    const cust = (customerItems || []).map((r) => ({
      _src: 'customer',
      id: r.id,
      title: r.service_type,
      who: r.customer_name,
      sub: `Preferred ${r.preferred_date}`,
      description: r.description,
      notes: r.customer_notes,
      photos: r.photos,
      status: (r.status || 'pending').toLowerCase(),
      priority: null,
      created_at: r.created_at,
      raw: r,
    }));
    const pm = (pmItems || []).map((r) => ({
      _src: 'pm',
      id: r.id,
      title: `${r.property_name || 'Property'} — ${r.work_order_number || ''}`.trim(),
      who: r.submitted_by_name || 'PM',
      sub: r.unit_number ? `Unit ${r.unit_number}` : 'Property-wide',
      description: r.issue_description,
      notes: r.access_notes,
      photos: [],
      status: r.status,
      priority: r.priority,
      created_at: r.created_at,
      raw: r,
    }));
    const all = [...cust, ...pm];
    return all.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [customerItems, pmItems]);

  const filtered = useMemo(() => {
    return unified.filter((row) => {
      if (source !== 'all' && row._src !== source) return false;
      if (filter === 'all') return true;
      if (row._src === 'customer') return row.status === filter;
      // PM mapping: open / completed / cancelled buckets
      if (filter === 'open') return pmOpenStatuses.has(row.status);
      if (filter === 'completed') return ['Completed', 'Invoiced', 'Paid'].includes(row.status);
      return false;
    });
  }, [unified, filter, source]);

  return (
    <div data-testid="admin-service-requests-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Service Requests</h2>
          <p className="text-[#A3A3A3]">All inbound service requests — customers <span className="text-[#D4AF37]">and</span> property managers — in one place.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="sr-source-filter" className="bg-[#141414] border-[#262626] text-white w-44"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="all" className="text-white">All sources</SelectItem>
              <SelectItem value="customer" className="text-white">Customer</SelectItem>
              <SelectItem value="pm" className="text-white">Property Manager</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="sr-filter" className="bg-[#141414] border-[#262626] text-white w-44"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="all" className="text-white">All</SelectItem>
              <SelectItem value="pending" className="text-white">Pending (customer)</SelectItem>
              <SelectItem value="scheduled" className="text-white">Scheduled (customer)</SelectItem>
              <SelectItem value="open" className="text-white">Open (PM)</SelectItem>
              <SelectItem value="completed" className="text-white">Completed</SelectItem>
              <SelectItem value="cancelled" className="text-white">Cancelled (customer)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-[#A3A3A3]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="sr-empty" className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">
          No service requests for this filter.
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((r) => (
            <div key={`${r._src}-${r.id}`} data-testid={`sr-row-${r.id}`} className={`bg-[#141414] border rounded-md p-5 ${r.priority === 'Emergency' ? 'border-[#EF4444]/60' : 'border-[#262626]'}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r._src === 'pm' ? (
                      <Building2 className="w-4 h-4 text-[#D4AF37]" />
                    ) : (
                      <PhoneCall className="w-4 h-4 text-[#D4AF37]" />
                    )}
                    <p className="text-white font-medium">{r.title}</p>
                    {r._src === 'pm' ? (
                      <Badge data-testid={`sr-source-badge-${r.id}`} className="bg-[#D4AF37]/15 text-[#D4AF37] border-transparent">PM PORTAL</Badge>
                    ) : (
                      <Badge data-testid={`sr-source-badge-${r.id}`} className="bg-blue-500/10 text-blue-300 border-transparent">CUSTOMER</Badge>
                    )}
                    {r.priority === 'Emergency' && (
                      <Badge className="bg-[#EF4444]/15 text-[#EF4444] border-transparent"><Siren className="w-3 h-3 mr-1 inline" />EMERGENCY</Badge>
                    )}
                    {r._src === 'customer' ? (
                      <Badge className={`${statusStyles[r.status] || statusStyles.pending} border-transparent`}>{(r.status || 'pending').toUpperCase()}</Badge>
                    ) : (
                      <Badge className="bg-[#262626] text-[#A3A3A3] border-transparent">{r.status?.toUpperCase()}</Badge>
                    )}
                    {Array.isArray(r.photos) && r.photos.length > 0 && (
                      <span className="flex items-center text-[#A3A3A3] text-xs"><Camera className="w-3 h-3 mr-1" /> {r.photos.length}</span>
                    )}
                  </div>
                  <p className="text-[#A3A3A3] text-sm mt-1">{r.who} · {r.sub}</p>
                  <p className="text-white text-sm mt-2">{r.description}</p>
                  {r.notes && <p className="text-[#A3A3A3] text-xs italic mt-1">{r._src === 'pm' ? 'Access' : 'Notes'}: {r.notes}</p>}
                  {r._src === 'customer' && Array.isArray(r.photos) && r.photos.length > 0 && (
                    <div className="mt-3 flex gap-2 flex-wrap">
                      {r.photos.map((p) => (
                        <a key={p.file_id} href={`${API}/service-requests/${r.id}/photos/${p.file_id}`} target="_blank" rel="noreferrer">
                          <img src={`${API}/service-requests/${r.id}/photos/${p.file_id}`} alt={p.filename}
                               className="w-20 h-20 object-cover rounded-md border border-[#262626] hover:border-[#D4AF37]" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 min-w-[160px]">
                  {r._src === 'customer' ? (
                    <Select value={r.status || 'pending'} onValueChange={(v) => updateStatus(r.id, v)}>
                      <SelectTrigger data-testid={`sr-status-${r.id}`} className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#141414] border-[#262626]">
                        <SelectItem value="pending" className="text-white">Pending</SelectItem>
                        <SelectItem value="scheduled" className="text-white">Scheduled</SelectItem>
                        <SelectItem value="completed" className="text-white">Completed</SelectItem>
                        <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <button
                      data-testid={`sr-open-pm-${r.id}`}
                      onClick={() => navigate('/admin/pm/service-requests')}
                      className="h-10 px-3 bg-[#D4AF37] text-[#0A0A0A] rounded font-medium hover:bg-[#F3E5AB] text-sm"
                    >
                      Manage in PM
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminServiceRequests;
